import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Globe, Instagram, Twitter, MapPin, Users } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

const REGION_NAMES = {
  uk: "United Kingdom",
  north_america: "North America",
  south_america: "South America",
  europe: "Europe",
  africa: "Africa",
  asia: "Asia",
  oceania: "Oceania",
};

export default function FanClubDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const initial = readSsgInitialData(slug);
  const [club, setClub] = useState(initial && initial.name ? initial : null);
  const [loading, setLoading] = useState(!(initial && initial.name));
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let alive = true;
    const fresh = readSsgInitialData(slug);
    setClub(fresh && fresh.name ? fresh : null);
    setLoading(!(fresh && fresh.name));
    setNotFound(false);
    axios
      .get(`${API}/fan-clubs/by-slug/${slug}`)
      .then((res) => alive && (setClub(res.data), setLoading(false)))
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 404) setNotFound(true);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading || (!club && !notFound)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading..." path={`/fan-clubs/${slug}`} />
        <div className="flex items-center justify-center py-40"><div className="spinner" /></div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="fan-club-notfound">
        <Navbar />
        <SEO title="Fan club not found" path={`/fan-clubs/${slug}`} noindex={true} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            FAN CLUB NOT FOUND
          </h1>
          <Link to="/fan-clubs" className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all fan clubs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const region = REGION_NAMES[club.region] || club.region;
  const descriptionText = club.description || club.summary || "";
  const metaDesc = (descriptionText || `${club.name} — a West Ham United supporters club in ${region}.`).slice(0, 160).replace(/\s+/g, " ").trim();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: club.name,
    description: descriptionText || undefined,
    url: `${BASE_URL}/fan-clubs/${club.slug}`,
    sameAs: [club.website_url, club.instagram_url, club.twitter_url].filter(Boolean),
    areaServed: region,
    memberOf: { "@type": "SportsTeam", name: "West Ham United" },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fan-club-detail-page">
      <SEO
        title={`${club.name} — West Ham Fan Club in ${region}`}
        path={`/fan-clubs/${club.slug}`}
        description={metaDesc}
        type="profile"
        jsonLd={jsonLd}
        seoTitle={club.seo_title}
        seoDescription={club.seo_description}
        keywords={club.seo_keywords}
        imageAlt={club.image_alt}
      />
      <Navbar />

      <div className="bg-[#7C2C3B] py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-3"
            data-testid="fan-club-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 text-[#F8D742] text-xs font-semibold uppercase tracking-wide mb-2">
            <MapPin className="w-3.5 h-3.5" /> {region}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white" style={{ fontFamily: "Barlow Condensed" }}>
            {club.name?.toUpperCase()}
          </h1>
          {club.summary && <p className="text-white/80 mt-2 leading-relaxed">{club.summary}</p>}
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {club.description && (
          <section className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
            <h2 className="text-xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: "Barlow Condensed" }}>
              ABOUT THIS CLUB
            </h2>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{club.description}</p>
          </section>
        )}

        <section className="bg-white border border-slate-200 rounded-xl p-6 mb-6">
          <h2 className="text-xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "Barlow Condensed" }}>
            CONNECT
          </h2>
          <div className="flex flex-wrap gap-3">
            {club.website_url && (
              <a
                href={club.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#2BAEE5] hover:bg-[#1a8fc4] text-white rounded-lg font-medium transition-colors"
                data-testid="fan-club-website"
              >
                <Globe className="w-4 h-4" /> Visit website
              </a>
            )}
            {club.instagram_url && (
              <a
                href={club.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
                data-testid="fan-club-instagram"
              >
                <Instagram className="w-4 h-4" /> Instagram
              </a>
            )}
            {club.twitter_url && (
              <a
                href={club.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-700 text-white rounded-lg font-medium transition-colors"
                data-testid="fan-club-twitter"
              >
                <Twitter className="w-4 h-4" /> X / Twitter
              </a>
            )}
            {!club.website_url && !club.instagram_url && !club.twitter_url && (
              <p className="text-sm text-slate-500">No public links provided for this club yet.</p>
            )}
          </div>
        </section>

        <div className="mt-8">
          <SocialShareButtons shareText={`${club.name} — West Ham Fan Club in ${region}`} />
        </div>
      </article>

      <Footer />
    </div>
  );
}
